import React from 'react';
import { Car, CarFront, Crown, Bus, MapPin, ShieldCheck, UserCheck, Receipt } from 'lucide-react';
import SEO from '../../components/SEO';
import { SitePage, PageHero, SectionHead, CtaBand } from '../../components/site/ui';
import { inr, unsplash } from '../../components/site/brand';
import ServiceEnquiry from '../../components/site/ServiceEnquiry';

const FLEET = [
  { Icon: Car, title: 'Sedan', seats: '4 seater', from: 2500, text: 'City tours, airport transfers and short trips.' },
  { Icon: CarFront, title: 'SUV / MUV', seats: '6–7 seater', from: 4500, text: 'Family trips and hill stations, with room for luggage.' },
  { Icon: Crown, title: 'Luxury', seats: '4 seater', from: 8000, text: 'Premium cars for weddings, VIPs and special days.' },
  { Icon: Bus, title: 'Tempo Traveller', seats: '12–17 seater', from: null, text: 'Groups, pilgrimages and corporate outings.' },
];

const CarRental = () => (
  <SitePage>
    <SEO title="Car Rental with Driver" description="Chauffeur-driven sedans, SUVs, luxury cars and tempo travellers for airport transfers, city tours and outstation trips." path="/travel-services/car-rental" image={unsplash('photo-1791217772860-1be726ffa335', 1200)} />
    <PageHero
      image={unsplash('photo-1791217772860-1be726ffa335', 2000)}
      imageAlt="Car on a misty mountain road"
      crumbs={[{ label: 'Home', to: '/' }, { label: 'Services' }, { label: 'Car rental' }]}
      kicker="Chauffeur-driven"
      title="Car"
      accent="rental"
      lede="Clean cars and experienced drivers for airport pickups, city sightseeing and outstation trips — book by the day or for your whole tour."
    >
      <a className="vk-btn vk-btn--hot" href="#enquire">Book a car</a>
      <a className="vk-btn vk-btn--ghost" href="#fleet">See the fleet</a>
    </PageHero>

    <section className="vk-section vk-section--sand" id="fleet" style={{ scrollMarginTop: 'calc(var(--header-h) + 12px)' }}>
      <div className="vk-wrap">
        <SectionHead kicker="Our fleet" title="Pick your ride" />
        <div className="vk-feats">
          {FLEET.map(({ Icon, title, seats, from, text }) => (
            <a key={title} className="vk-feat vk-feat--link" href="#enquire">
              <Icon size={30} />
              <h3>{title}</h3>
              <p style={{ fontWeight: 700, color: 'var(--ink)', margin: '0 0 6px' }}>{seats}</p>
              <p>{text}</p>
              <p className="vk-feat__price">{from ? <>From <b>{inr(from)}</b> / day</> : <>Price <b>on request</b></>}</p>
            </a>
          ))}
        </div>
        <p className="vk-form__note" style={{ marginTop: 16 }}>Indicative day rates; fuel, tolls, parking and state permits are extra unless quoted otherwise.</p>
      </div>
    </section>

    <section className="vk-section">
      <div className="vk-wrap">
        <SectionHead kicker="Why book with us" title="Simple, safe, on time" />
        <div className="vk-feats">
          <div className="vk-feat"><ShieldCheck size={28} /><h3>Well-kept cars</h3><p>Regularly serviced and cleaned before every trip.</p></div>
          <div className="vk-feat"><UserCheck size={28} /><h3>Professional drivers</h3><p>Licensed, courteous and familiar with the routes.</p></div>
          <div className="vk-feat"><MapPin size={28} /><h3>Flexible pickup</h3><p>Airport, railway station, hotel or home.</p></div>
          <div className="vk-feat"><Receipt size={28} /><h3>Clear pricing</h3><p>Everything written in the quote — no surprises.</p></div>
        </div>
      </div>
    </section>

    <ServiceEnquiry
      title="Book a car"
      kicker="Car booking"
      productType="car_rental"
      subject="Hi VK Tours, I'd like to book a car."
      fields={[
        { key: 'vehicle', label: 'Vehicle', options: FLEET.map((v) => v.title), placeholder: 'Any', required: true },
        { key: 'days', label: 'Number of days', type: 'number' },
        { key: 'pickup', label: 'Pickup city / point', placeholder: 'e.g. Vijayawada airport', required: true },
        { key: 'destination', label: 'Going to', placeholder: 'e.g. Araku, Tirupati' },
      ]}
      points={['Local, outstation and airport transfers', 'Driver allowance and night charges shown in the quote', 'Cars available across our tour destinations']}
    />

    <CtaBand />
  </SitePage>
);

export default CarRental;
